import {
  RULES,
  cloneState,
  getCard,
  getCardTargets,
  getDevotion,
  getDevotionTargets,
  getReplayableCard,
  getServant,
  getStage,
  legalFocusLanes,
  validatePlan,
  type CardDefinition,
  type Depth,
  type DevotionDefinition,
  type GameState,
  type HourResolution,
  type Lane,
  type PlayerIndex,
  type PlayerPlan,
} from "../../shared/index.js";
import type {
  OnlineCredentials,
  OnlineRoomSnapshot,
} from "../../shared/protocol/index.js";
import { OnlineApiClient } from "../online/OnlineApiClient.js";
import type {
  BattlePhase,
  BattleSession,
  BattleUiState,
} from "./BattleSession.js";

const POLL_INTERVAL_MS = 700;

export class OnlineBattleSession implements BattleSession {
  public readonly mode = "online" as const;
  public readonly viewerIndex: PlayerIndex;
  public state: GameState;
  public draft: PlayerPlan;
  public phase: BattlePhase;
  public error = "";

  private presentationState: GameState | null = null;
  private snapshot: OnlineRoomSnapshot;
  private resolutionVersion: number;
  private heartbeat: ReturnType<typeof setInterval> | null = null;
  private readonly listeners = new Set<() => void>();
  private disposed = false;

  public constructor(
    snapshot: OnlineRoomSnapshot,
    public readonly credentials: OnlineCredentials,
    private readonly api = new OnlineApiClient(),
  ) {
    if (!snapshot.state) throw new Error("Online battle has not started.");
    this.snapshot = snapshot;
    this.viewerIndex = credentials.playerIndex;
    this.state = cloneState(snapshot.state);
    this.resolutionVersion = snapshot.resolutionVersion;
    this.phase = this.phaseFromSnapshot(snapshot);
    this.draft = this.createDraft();
    this.heartbeat = setInterval(() => void this.refreshHeartbeat(), 1_500);
  }

  public get startLog(): string {
    const stage = getStage(this.state.stageId);
    const opponent = this.state.players[this.opponentIndex];
    return "《" + (stage?.name ?? "共有された窓") + "》で、" + opponent.name + "との一夜が始まります。";
  }

  public get selectedCard(): CardDefinition | null {
    return this.draft.supplyCardId ? getCard(this.draft.supplyCardId) : null;
  }

  public get selectedDevotion(): DevotionDefinition | null {
    return this.draft.devotionId ? getDevotion(this.draft.devotionId) : null;
  }

  private get opponentIndex(): PlayerIndex {
    return this.viewerIndex === 0 ? 1 : 0;
  }

  public reset(): void {
    this.error = "オンライン対戦では、結果画面から部屋を退出してください。";
    this.notify();
  }

  public subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  public dispose(): void {
    this.disposed = true;
    if (this.heartbeat) clearInterval(this.heartbeat);
    this.heartbeat = null;
    this.listeners.clear();
  }

  public async leave(): Promise<void> {
    this.dispose();
    await this.api.leave(this.credentials);
  }

  public selectCard(cardId: string): void {
    if (this.phase !== "planning" || !this.isSupplyCardSelectable(cardId)) return;
    this.error = "";
    if (this.draft.supplyCardId === cardId) {
      delete this.draft.supplyCardId;
      this.draft.target = {};
      return;
    }
    this.draft.cardInstanceId = "";
    this.draft.supplyCardId = cardId;
    this.draft.devotionId = "";
    this.draft.target = {};
  }

  public selectDevotion(): void {
    if (this.phase !== "planning") return;
    const player = this.state.players[this.viewerIndex];
    const servant = getServant(player.servantId);
    if (!servant || player.devotionUsed
      || getDevotionTargets(this.state, this.viewerIndex).length === 0) return;
    this.error = "";
    if (this.draft.devotionId === servant.devotion.id) {
      this.draft.devotionId = "";
      this.draft.target = {};
      return;
    }
    this.draft.cardInstanceId = "";
    delete this.draft.supplyCardId;
    this.draft.devotionId = servant.devotion.id;
    this.draft.target = {};
  }

  public selectDevotionTarget(playerIndex: PlayerIndex): void {
    if (this.phase !== "planning" || this.selectedDevotion?.targetType !== "player") return;
    this.error = "";
    this.draft.target = { playerIndex };
  }

  public selectFocus(lane: Lane): void {
    if (this.phase !== "planning") return;
    if (!legalFocusLanes(this.state, this.viewerIndex).includes(lane)) {
      this.error = "現在の注目からは、隣のレーンまでしか移れません。";
      return;
    }
    this.error = "";
    this.draft.focusLane = lane;
  }

  public selectBoardPosition(lane: Lane, depth: Depth, objectId?: string): void {
    if (this.phase !== "planning") return;
    const devotion = this.selectedDevotion;
    const card = this.selectedCard
      ?? (devotion?.targetType === "replay"
        ? getReplayableCard(this.state, this.viewerIndex)
        : null);
    this.error = "";

    if (devotion?.targetType === "object") {
      if (objectId) this.draft.target = { objectId };
      return;
    }
    if (card?.kind === "object") {
      this.draft.target = { lane };
      return;
    }
    if (!card || card.kind !== "spell") {
      this.selectFocus(lane);
      return;
    }
    if (card.targetType === "none") {
      this.draft.target = {};
      return;
    }
    if (card.targetType === "lane") {
      this.draft.target = { lane };
      return;
    }
    if (card.targetType === "depth") {
      this.draft.target = { depth };
      return;
    }
    if (card.targetType === "object") {
      if (objectId) this.draft.target = { objectId };
      return;
    }

    const source = this.state.board.find((object) => object.id === this.draft.target.objectId);
    if (source) {
      if (card.targetType === "object-direction") {
        const direction = lane - source.lane;
        if (depth === source.depth && (direction === -1 || direction === 1)) {
          this.draft.target = { objectId: source.id, direction };
          return;
        }
      }
      if (card.targetType === "object-depth"
        && lane === source.lane
        && Math.abs(depth - source.depth) === 1) {
        this.draft.target = { objectId: source.id, depth };
        return;
      }
      if (objectId === source.id) return;
    }
    if (objectId) this.draft.target = { objectId };
  }

  public async prepareResolution(isPass: boolean): Promise<HourResolution | null> {
    if (this.phase !== "planning" || this.state.result) return null;
    const proposed = isPass ? this.passPlan() : clonePlan(this.draft);
    const validation = validatePlan(this.state, this.viewerIndex, proposed);
    if (!validation.ok) {
      this.error = validation.reason;
      return null;
    }

    this.error = "";
    this.presentationState = cloneState(this.state);
    this.phase = "waiting";
    this.notify();
    const baselineVersion = this.resolutionVersion;

    try {
      let snapshot = await this.api.submit({ ...this.credentials, plan: proposed });
      this.updateSnapshotMetadata(snapshot);
      this.notify();
      while (!this.disposed && snapshot.resolutionVersion <= baselineVersion) {
        if (snapshot.status === "waiting") {
          throw new Error("相手が退室しました。新しい参加者を待っています。");
        }
        await delay(POLL_INTERVAL_MS);
        snapshot = await this.api.getState(this.credentials);
        this.updateSnapshotMetadata(snapshot);
      }
      if (this.disposed) return null;
      if (!snapshot.state || !snapshot.latestResolution) {
        throw new Error("解決結果を受け取れませんでした。");
      }
      this.snapshot = snapshot;
      this.state = cloneState(snapshot.state);
      this.resolutionVersion = snapshot.resolutionVersion;
      this.phase = "resolving";
      this.error = "";
      return structuredClone(snapshot.latestResolution);
    } catch (error) {
      this.presentationState = null;
      this.phase = this.phaseFromSnapshot(this.snapshot);
      this.error = error instanceof Error ? error.message : "サーバーとの通信に失敗しました。";
      this.notify();
      return null;
    }
  }

  public finishResolution(): void {
    this.presentationState = null;
    this.phase = this.state.result ? "result" : "planning";
    this.draft = this.createDraft();
  }

  public getUiState(): BattleUiState {
    const displayState = this.presentationState ?? this.state;
    const actionSelected = Boolean(this.draft.supplyCardId || this.draft.devotionId);
    const actionValidation = validatePlan(this.state, this.viewerIndex, this.draft);
    const passValidation = validatePlan(this.state, this.viewerIndex, this.passPlan());
    const player = this.state.players[this.viewerIndex];
    const servant = getServant(player.servantId);
    const supply = this.state.openSupply;
    const displaySupply = displayState.openSupply;
    const opponent = this.opponentIndex;
    return {
      mode: this.mode,
      viewerIndex: this.viewerIndex,
      cardSource: "supply",
      state: cloneState(displayState),
      draft: clonePlan(this.draft),
      phase: this.phase,
      legalFocusLanes: legalFocusLanes(this.state, this.viewerIndex),
      allowedCardInstanceIds: [],
      supplyCards: (supply?.cards ?? []).map((entry) => ({
        cardId: entry.cardId,
        cost: entry.cost,
        limited: entry.limited,
        available: this.phase === "planning" && this.isSupplyCardSelectable(entry.cardId),
        opponentAvailable: entry.availableTo[opponent],
      })),
      energy: displaySupply ? [...displaySupply.energy] as [number, number] : null,
      maxEnergy: supply?.maxEnergy ?? RULES.openSupplyMaxEnergy,
      devotionAvailable: this.phase === "planning"
        && !player.devotionUsed
        && Boolean(servant)
        && getDevotionTargets(this.state, this.viewerIndex).length > 0,
      tutorialPrompt: this.phase === "waiting"
        ? this.snapshot.submitted[this.viewerIndex]
          ? "計画を提出しました。相手の計画を待っています。"
          : "対戦相手の入室を待っています。"
        : this.phase === "planning"
          ? "演出卓から一枚を選び、相手には伏せたまま計画を提出します。"
          : this.phase === "result" ? "夜明けです。" : "二人の計画を順番に処理しています。",
      targetPrompt: this.getTargetPrompt(),
      error: this.error,
      canSubmit: this.phase === "planning" && actionSelected && actionValidation.ok,
      canPass: this.phase === "planning" && passValidation.ok,
      resultKey: null,
      connected: this.snapshot.connected,
      submitted: this.snapshot.submitted,
    };
  }

  private async refreshHeartbeat(): Promise<void> {
    if (this.disposed || this.phase === "resolving") return;
    try {
      const snapshot = await this.api.getState(this.credentials);
      const previousGeneration = this.snapshot.generation;
      const hasNewResolution = snapshot.resolutionVersion > this.resolutionVersion;
      const changed = snapshot.generation !== previousGeneration
        || snapshot.status !== this.snapshot.status
        || hasNewResolution
        || snapshot.connected.some((value, index) => value !== this.snapshot.connected[index])
        || snapshot.submitted.some((value, index) => value !== this.snapshot.submitted[index]);
      if (snapshot.generation !== previousGeneration && snapshot.state) {
        this.state = cloneState(snapshot.state);
        this.resolutionVersion = snapshot.resolutionVersion;
        this.presentationState = null;
        this.phase = "planning";
        this.draft = this.createDraft();
        this.error = "";
      } else if (hasNewResolution && snapshot.state) {
        this.state = cloneState(snapshot.state);
        this.resolutionVersion = snapshot.resolutionVersion;
        this.presentationState = null;
        this.phase = snapshot.state.result ? "result" : "planning";
        this.draft = this.createDraft();
        this.error = "";
      } else if (snapshot.status === "waiting" && this.phase === "planning") {
        this.phase = "waiting";
        this.error = "相手が退室しました。新しい参加者を待っています。";
      } else if (snapshot.submitted[this.viewerIndex] && this.phase === "planning") {
        this.phase = "waiting";
        this.error = "";
      }
      this.snapshot = snapshot;
      if (changed) this.notify();
    } catch (error) {
      this.error = error instanceof Error ? error.message : "サーバーへ接続できません。";
      this.notify();
    }
  }

  private updateSnapshotMetadata(snapshot: OnlineRoomSnapshot): void {
    this.snapshot = snapshot;
  }

  private phaseFromSnapshot(snapshot: OnlineRoomSnapshot): BattlePhase {
    if (snapshot.status === "finished") return "result";
    if (snapshot.status === "waiting" || snapshot.submitted[this.viewerIndex]) return "waiting";
    return "planning";
  }

  private notify(): void {
    for (const listener of this.listeners) listener();
  }

  private createDraft(): PlayerPlan {
    return {
      focusLane: this.state.players[this.viewerIndex].focusLane,
      cardInstanceId: "",
      devotionId: "",
      target: {},
    };
  }

  private passPlan(): PlayerPlan {
    return {
      focusLane: this.draft.focusLane,
      cardInstanceId: "",
      devotionId: "",
      target: {},
    };
  }

  private isSupplyCardSelectable(cardId: string): boolean {
    const supply = this.state.openSupply;
    const entry = supply?.cards.find((item) => item.cardId === cardId);
    const card = getCard(cardId);
    if (!supply || !entry || !card || !entry.availableTo[this.viewerIndex]) return false;
    if (supply.energy[this.viewerIndex] < entry.cost) return false;
    return getCardTargets(this.state, card.id).length > 0;
  }

  private getTargetPrompt(): string {
    if (this.error) return this.error;
    if (this.phase === "waiting") return "提出内容は、両者が揃うまで相手には公開されません。";
    if (this.phase === "resolving") return "二人の計画を順番に夢へ反映しています。";
    if (this.phase === "result") return "夜明けを迎えました。";
    const devotion = this.selectedDevotion;
    if (devotion) {
      if (devotion.targetType === "none") return "献身《" + devotion.name + "》を使います。";
      if (devotion.targetType === "player") {
        return this.draft.target.playerIndex === undefined
          ? "献身を向けるご主人を選んでください。"
          : "選んだご主人へ献身《" + devotion.name + "》を使います。";
      }
      if (devotion.targetType === "replay") {
        const replay = getReplayableCard(this.state, this.viewerIndex);
        return replay ? "《" + replay.name + "》を対象を選び直して再演します。" : "再演できるカードがありません。";
      }
      const object = this.state.board.find((item) => item.id === this.draft.target.objectId);
      return object ? "《" + object.name + "》へ献身を使います。" : "献身を向ける夢を選んでください。";
    }
    const card = this.selectedCard;
    if (!card) return "演出卓からカードか献身を選んでください。";
    if (card.kind === "object") {
      return this.draft.target.lane
        ? "《" + card.name + "》をレーン" + this.draft.target.lane + "の深度3へ置きます。"
        : "置くレーンを盤面から選んでください。";
    }
    if (card.targetType === "none") return "《" + card.name + "》を盤面全体へ使います。";
    if (card.targetType === "lane") return this.draft.target.lane ? "対象はレーン" + this.draft.target.lane + "です。" : "対象のレーンを選んでください。";
    if (card.targetType === "depth") return this.draft.target.depth ? "対象は深度" + this.draft.target.depth + "です。" : "対象の深度を選んでください。";
    return this.draft.target.objectId ? "対象と移動先を確認してください。" : "対象の夢オブジェクトを選んでください。";
  }
}

function clonePlan(plan: PlayerPlan): PlayerPlan {
  return {
    focusLane: plan.focusLane,
    cardInstanceId: plan.cardInstanceId,
    ...(plan.supplyCardId ? { supplyCardId: plan.supplyCardId } : {}),
    devotionId: plan.devotionId,
    target: { ...plan.target },
  };
}

function delay(milliseconds: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, milliseconds));
}
